import { useTranslation } from 'react-i18next';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useEntityActivity, useTeamMemberActivity, type ActivityLog } from '@/hooks/useApi';

interface BaseProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
}

interface EntityProps extends BaseProps {
  mode: 'entity';
  entityType: string;
  entityId: string;
}

interface UserProps extends BaseProps {
  mode: 'user';
  userId: string;
}

type Props = EntityProps | UserProps;

const ACTION_COLORS: Record<string, string> = {
  created: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-blue-100 text-blue-700',
  status: 'bg-amber-100 text-amber-700',
  deleted: 'bg-red-100 text-red-700',
  uploaded: 'bg-violet-100 text-violet-700',
  converted: 'bg-cyan-100 text-cyan-700',
  reconciled: 'bg-teal-100 text-teal-700',
  confirmed: 'bg-teal-100 text-teal-700',
};

function badgeClass(action: string): string {
  for (const key of Object.keys(ACTION_COLORS)) {
    if (action.includes(key)) return ACTION_COLORS[key];
  }
  return 'bg-gray-100 text-gray-700';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString();
}

function userName(log: ActivityLog): string {
  if (!log.user) return 'System';
  const full = `${log.user.firstName} ${log.user.lastName}`.trim();
  return full || log.user.email;
}

export default function ActivityModal(props: Props) {
  const { t } = useTranslation();
  const entityQuery = useEntityActivity(
    props.mode === 'entity' ? props.entityType : null,
    props.mode === 'entity' ? props.entityId : null,
  );
  const userQuery = useTeamMemberActivity(props.mode === 'user' ? props.userId : null);

  const { data: logs, isLoading } = props.mode === 'entity' ? entityQuery : userQuery;

  return (
    <Transition appear show={props.isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={props.onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                  <Dialog.Title className="flex items-center gap-2 text-base font-semibold text-gray-900">
                    <ClockIcon className="w-5 h-5 text-gray-500" />
                    {props.title ?? t('activity.title')}
                  </Dialog.Title>
                  <button
                    onClick={props.onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="max-h-[70vh] overflow-y-auto p-5">
                  {isLoading && (
                    <div className="text-sm text-gray-500">{t('common.loading')}</div>
                  )}

                  {!isLoading && (!logs || logs.length === 0) && (
                    <div className="py-12 text-center text-sm text-gray-400">
                      {t('activity.empty')}
                    </div>
                  )}

                  {logs && logs.length > 0 && (
                    <ol className="relative space-y-4 border-l-2 border-gray-100 pl-5">
                      {logs.map((log) => (
                        <li key={log.id} className="relative">
                          <span className="absolute -left-[27px] top-1.5 block w-3 h-3 rounded-full bg-white border-2 border-primary-400" />
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${badgeClass(log.action)}`}
                            >
                              {log.action}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {userName(log)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatDate(log.createdAt)}
                            </span>
                          </div>
                          {props.mode === 'user' && log.entityType && (
                            <div className="mt-0.5 text-xs text-gray-500">
                              {log.entityType}
                              {log.entityId ? ` · ${log.entityId.slice(0, 8)}` : ''}
                            </div>
                          )}
                          {log.description && (
                            <div className="mt-1 text-sm text-gray-600">{log.description}</div>
                          )}
                          {log.newValues && Object.keys(log.newValues).length > 0 && (
                            <pre className="mt-1.5 text-[11px] text-gray-500 bg-gray-50 border border-gray-100 rounded p-2 overflow-x-auto">
                              {JSON.stringify(log.newValues, null, 2)}
                            </pre>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
